import React, { Component } from 'react';
import { Form, Input, Button, Popconfirm, Icon, Layout, Card, Alert } from 'antd';
import { ActiveSsoRequest } from 'models/request/ActiveSsoRequest';
import { PushNotification } from 'utils/PushNotification';
import { activeSso } from '../../../services/CeberusApiService';
import RestartAccountModal from '../../../components/RestartAccountModal';
import './Sso.scss';

type FormState = {
  isLoading: boolean;
  lastActivation: string | null;
  ssoActivated: boolean;
  isRestartModalVisible: boolean;
};

const PROVIDER_TYPE = 'AzureOpenId';

class SsoComponent extends Component<any, FormState> {
  constructor(props: any) {
    super(props);
    this.state = {
      isLoading: false,
      lastActivation: null,
      ssoActivated: false,
      isRestartModalVisible: false,
    };
  }

  handleOnActiveSso = async (error: any, requestBody: ActiveSsoRequest): Promise<void> => {
    const { form } = this.props;

    try {
      requestBody.ProviderType = PROVIDER_TYPE;

      const response = await activeSso(requestBody);

      PushNotification.success('✅ SSO configuration has been activated successfully!');

      this.setState({
        lastActivation: new Date().toLocaleString(),
        ssoActivated: true,
      });

      form.resetFields();

    } catch (httpError) {
      PushNotification.httpError(httpError);
    }

    if (error) {
      this.setState({ isLoading: false });
      PushNotification.error('Some fields are invalid, check values and try again');
    }

    this.setState({ isLoading: false })
  }

  showRestartModal = () => {
    this.setState({ isRestartModalVisible: true });
  }

  hideRestartModal = () => {
    this.setState({ isRestartModalVisible: false });
  }

  handleSubmit = (event: any): void => {
    const { form } = this.props;
    this.setState({ isLoading: true });
    event.preventDefault();
    form.validateFields(this.handleOnActiveSso)
  }

  render() {

    const { getFieldDecorator } = this.props.form;
    const { lastActivation, ssoActivated } = this.state;

    return (
      <div className="flex flex-wrap">
        <div className="tc w-100">
          <h1 className="sso-management__header">SSO Management</h1>
          <Layout.Content className="sso-management__container">

            <Card
              title={<span><Icon type="setting" /> Step 1: Activate SSO Configuration</span>}
              className="sso-management__card"
            >
              <Alert
                message="Safe Operation"
                description="This step only configures SSO settings and does not restart any services. It can be performed at any time."
                type="info"
                showIcon
                className="sso-management__alert"
              />

              <Form onSubmit={this.handleSubmit}>
                <Form.Item label="Organization Id">
                  {getFieldDecorator('ContractId', {
                    rules: [
                      {
                        required: true,
                        message: 'You need to fill organization id with 2 - 30 lowercase letters.',
                        pattern: '^[a-z][a-z0-9-]{1,29}$',
                      },
                      {
                        validator: (_, value, callback) => {
                          if (/^com[1-9]$/.test(value)) {
                            callback("Organization id cannot be 'com + number'");
                          } else {
                            callback();
                          }
                        },
                      },
                    ],
                  })(<Input placeholder="e.g., my-organization" />)}
                </Form.Item>

                <Form.Item label="Client Id">
                  {getFieldDecorator('ClientId', {
                    rules: [
                      {
                        required: true,
                        message: 'You need to fill client id.',
                      },
                    ],
                  })(<Input placeholder="Azure AD Client ID" />)}
                </Form.Item>

                <Form.Item label="Endpoint Metadata">
                  {getFieldDecorator('EndpointMetadata', {
                    rules: [
                      {
                        required: true,
                        message: 'You need to fill endpoint metadata.',
                      },
                    ],
                  })(<Input placeholder="https://..." />)}
                </Form.Item>

                {lastActivation && (
                  <Alert
                    message={`Last SSO activation: ${lastActivation}`}
                    type="success"
                    className="sso-management__last-activation"
                  />
                )}

                <Form.Item>
                  <Popconfirm
                    title="Are you sure you want to activate SSO configuration?"
                    onConfirm={this.handleSubmit}
                    okText="Yes, Activate"
                    cancelText="Cancel"
                  >
                    <Button
                      type="primary"
                      loading={this.state.isLoading}
                      htmlType="submit"
                      icon="setting"
                      size="large"
                    >
                      Activate SSO Configuration
                    </Button>
                  </Popconfirm>
                </Form.Item>
              </Form>
            </Card>

            <Card
              title={<span><Icon type="reload" /> Step 2: Restart Account</span>}
              className="sso-management__card"
            >
              <p>After activating SSO configuration, restart the Account to apply the changes.</p>

              <Button
                type="primary"
                icon="reload"
                size="large"
                onClick={this.showRestartModal}
              >
                Restart Account
              </Button>
            </Card>

          </Layout.Content>
        </div>

        <RestartAccountModal
          visible={this.state.isRestartModalVisible}
          onCancel={this.hideRestartModal}
        />
      </div>
    );
  }
}

export default Form.create()(SsoComponent);
